import { DATE_TYPES } from '@/utils/constants';
import { handleRequests, showErrorMessage } from '@/helpers';

export const AgGridMixin = {
    methods: {
        /**
         * Creates an ag-Grid Server-Side Row Model datasource object.
         * Dynamically pulls the latest search text (via searchTextValueFn) each time rows are requested,
         * builds a server request (paging, sorting, filtering, global search), posts it to {gridUrl}/grid/search,
         * optionally transforms the returned row array (dataTransformerFn), and feeds results to ag-Grid.
         *
         * Handles:
         *  - Loading overlay display/hide
         *  - Error notification + params.fail()
         *
         * @param {string} gridUrl Base API url (without trailing /grid/search)
         * @param {object} gridApi ag-Grid API instance (used for overlay control)
         * @param {Function} searchTextValueFn Zero-arg function returning current global search string
         * @param {Function|null} dataTransformerFn Optional function: (responses:Array) => transformed Array
         * @param {Function|null} grandTotalsCallbackFn Optional callback: (grandTotals:Object) => void - called with grand totals from server
         * @returns {{ getRows: function(Object): Promise<void> }} ag-Grid datasource object
         */
        $_createDataSource(gridUrl, gridApi, searchTextValueFn, dataTransformerFn, grandTotalsCallbackFn) {
            // eslint-disable-next-line no-unused-expressions
            return {
                getRows: async (params) => {
                    try {
                        gridApi.showLoadingOverlay();

                        const searchText = searchTextValueFn();
                        const request = this.buildServerRequest(params.request, searchText);
                        const endpoint = `${gridUrl}/grid/search`;

                        const response = await handleRequests(endpoint, {
                            method: 'POST',
                            data: request
                        });

                        const { responses, totalRecordCount, grandTotals } = response.data;

                        let transformedData = responses;

                        if (dataTransformerFn != null) {
                            transformedData = dataTransformerFn(responses);
                        }

                        // Notify the component about grand totals if callback is provided and totals are non-empty
                        if (grandTotalsCallbackFn != null && grandTotals != null && Object.keys(grandTotals).length > 0) {
                            grandTotalsCallbackFn(grandTotals);
                        }

                        params.success({
                            rowData: transformedData,
                            rowCount: totalRecordCount
                        });

                        gridApi.hideOverlay();
                    } catch (error) {
                        console.error('Error loading server-side data:', error);

                        const message = 'Error loading data. Please try again.';
                        showErrorMessage(this, message, error);

                        params.fail();
                    }
                }
            };
        },
        buildServerRequest(request, searchTextValue) {
            const serverRequest = {
                startRow: request.startRow,
                endRow: request.endRow,
                sortModel: request.sortModel,
                filterModel: request.filterModel,
                searchAllFields: searchTextValue
            };

            return serverRequest;
        },
        $_formatDateFromUTC(date) {
            if (!this.isDateValid(date)) {
                return '';
            }

            return this.$options.filters.utcDateFormat(date, DATE_TYPES.standardDate);
        },
        $_formatDate(date) {
            if (!this.isDateValid(date)) {
                return '';
            }

            return this.$options.filters.dateFormat(date, DATE_TYPES.standardDate);
        },
        isDateValid(date) {
            if (date === null || date === undefined || date === '') {
                return false;
            }
            return true;
        },
        $_linkCellRenderer(pageName, pageParams, linkText) {
            return {
                pageName,
                pageParams,
                linkText
            };
        },
        // This is used to format the keyCreator for filter set dropdown columns in ag grid that uses int as an Id
        $_formatKeyCreator(value, id) {
            if (value != null && id == null)
                return null;

            return value ? id.toString() : '';
        },
        $_currencyFormatter(params) {
            return params.value == null
                ? ''
                : this.$options.filters?.currency?.(params.value) ?? Number(params.value).toFixed(2);
        },
        $_decimalFormatter(params) {
            return params.value == null ? '' : Number(params.value).toFixed(2);
        },
        $_dateFormatter(params) {
            return params.value ? new Date(params.value).toLocaleDateString() : '';
        },
        $_dateTimeFormatter(params) {
            return params.value ? new Date(params.value).toLocaleString() : '';
        },
        $_percentFormatter(params) {
            return params.value == null ? '' : `${Number(params.value).toFixed(2)}%`;
        },
        $_booleanFormatter(params) {
            if (params.value == null) {
                return '';
            }
            return params.value ? 'Yes' : 'No';
        },
        $_timeFormatter(params) {
            if (params.value == null) {
                return '';
            }

            return this.$options.filters.timeFormat(params.value, DATE_TYPES.standardTime, true);
        },
        $_resolveFilter(type) {
            if (type == null) {
                return false;
            }

            if (type === 'date' || type === 'datetime') {
                return 'agDateColumnFilter';
            }
            if (type === 'number' || type === 'currency' || type === 'percent') {
                return 'agNumberColumnFilter';
            }
            if (type === 'boolean') {
                return 'agSetColumnFilter';
            }
            return 'agTextColumnFilter';
        },
        $_updatePageTotals(api, keysToSum) {
            if (!api || !keysToSum || keysToSum.length === 0) {
                return;
            }
            const totals = keysToSum.reduce((acc, key) => {
                acc[key] = 0;
                return acc;
            }, {});

            const pageSize = api.paginationGetPageSize();
            const currentPage = api.paginationGetCurrentPage();
            const totalDisplayed = api.getDisplayedRowCount();

            const startIndex = currentPage * pageSize;
            const endIndex = Math.min(startIndex + pageSize, totalDisplayed);

            for (let i = startIndex; i < endIndex; i++) {
                const node = api.getDisplayedRowAtIndex(i);

                if (!node || !node.data) {
                    // eslint-disable-next-line no-continue
                    continue;
                }

                const d = node.data;

                // eslint-disable-next-line no-restricted-syntax
                for (const key of keysToSum) {
                    if (typeof d[key] === 'number') {
                        totals[key] += d[key];
                    }
                }
            }

            const totalsRow = [totals];
            api.setPinnedBottomRowData(totalsRow);
        },
        /**
         * Sets the grand totals from server response as a pinned bottom row in the grid.
         * This displays totals for all filtered records, not just the current page.
         *
         * @param {object} api ag-Grid API instance
         * @param {object} grandTotals Object containing property names (camelCase) as keys and their sums as values
         * @param {string} labelField Optional field name to show "Grand Total" label (defaults to first text column)
         */
        $_setGrandTotals(api, grandTotals, labelField = null) {
            if (!api || !grandTotals || Object.keys(grandTotals).length === 0) {
                return;
            }

            // Create the pinned row data with grand totals
            const pinnedRowData = { ...grandTotals };

            // Add a label to identify this as the grand total row
            if (labelField) {
                pinnedRowData[labelField] = 'Grand Total';
            }

            // Mark this row as a grand total row for styling purposes
            // eslint-disable-next-line no-underscore-dangle
            pinnedRowData._isGrandTotal = true;

            api.setPinnedBottomRowData([pinnedRowData]);
        },
        /**
         * Updates the pinned bottom rows to show both page totals and grand totals.
         *
         * @param {object} api ag-Grid API instance
         * @param {object} grandTotals Object containing grand totals from server (or null if not yet loaded)
         * @param {Array<string>} keysToSum Array of property names (camelCase) to sum for page totals
         * @param {string} labelField Field name to show "Page Total" / "Grand Total" labels
         */
        $_updatePinnedBottomRowsWithGrandTotals(api, grandTotals, keysToSum, labelField) {
            if (!api) {
                return;
            }

            const pinnedRows = [];

            // Calculate page totals
            const pageTotals = this.$_calculatePageTotals(api, keysToSum);
            if (pageTotals) {
                pageTotals[labelField] = 'Page Total';
                // eslint-disable-next-line no-underscore-dangle
                pageTotals._isPageTotal = true;
                pinnedRows.push(pageTotals);
            }

            // Add grand totals if available and non-empty
            if (grandTotals && Object.keys(grandTotals).length > 0) {
                const grandTotalRow = { ...grandTotals };
                grandTotalRow[labelField] = 'Grand Total';
                // eslint-disable-next-line no-underscore-dangle
                grandTotalRow._isGrandTotal = true;
                pinnedRows.push(grandTotalRow);
            }

            api.setPinnedBottomRowData(pinnedRows);
        },
        /**
         * Calculates page totals for the specified keys by summing values on the current page.
         *
         * @param {object} api ag-Grid API instance
         * @param {Array<string>} keysToSum Array of property names (camelCase) to sum
         * @returns {object|null} Object with sums for each key, or null if api is not available
         */
        $_calculatePageTotals(api, keysToSum) {
            if (!api || !keysToSum || keysToSum.length === 0) {
                return null;
            }

            const totals = keysToSum.reduce((acc, key) => {
                acc[key] = 0;
                return acc;
            }, {});

            const pageSize = api.paginationGetPageSize();
            const currentPage = api.paginationGetCurrentPage();
            const totalDisplayed = api.getDisplayedRowCount();

            const startIndex = currentPage * pageSize;
            const endIndex = Math.min(startIndex + pageSize, totalDisplayed);

            for (let i = startIndex; i < endIndex; i++) {
                const node = api.getDisplayedRowAtIndex(i);

                if (!node || !node.data) {
                    // eslint-disable-next-line no-continue
                    continue;
                }

                const d = node.data;

                // eslint-disable-next-line no-restricted-syntax
                for (const key of keysToSum) {
                    if (typeof d[key] === 'number') {
                        totals[key] += d[key];
                    }
                }
            }

            return totals;
        }
    }
};
